import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, renderSlot as _renderSlot, createVNode as _createVNode, createElementVNode as _createElementVNode, withModifiers as _withModifiers, mergeProps as _mergeProps, withDirectives as _withDirectives, vShow as _vShow, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue";
const _hoisted_1 = ["textContent"];
const _hoisted_2 = ["disabled", "aria-disabled", "tabindex"];
const _hoisted_3 = {
    key: 2,
    class: "rec-flyout-indicator"
};
const _hoisted_4 = ["id", "role"];
import { toCssSizeUnit } from "@/common/helpers/style";
import { vTooltip } from "@/common/directives/tip-directive";
import { onClickOutside, useElementBounding, useRafFn, useScreenOrientation, useVModel, useWindowSize } from "@vueuse/core";
import { v4 as uuid } from "uuid";
import { useDropdownAccessibility } from "@/common/composables/dropdown-accessibility";
import { computed, onMounted, ref, watch, watchEffect } from "vue";
// Components
import SvgIcon from "@/common/components/SvgIcon.vue";
import { MenuItemType } from "@/common/composables/dropdown-accessibility";
//Props
const FLYOUT_OFFSET_GAP = 12;
/**
 * Calculate the base Y position
 */
export default /*@__PURE__*/ _defineComponent({
    __name: 'DropdownMenu',
    props: {
        absolute: { type: Boolean },
        appendIcon: { default: "" },
        appendIconInside: { default: "" },
        autoClose: { type: Boolean, default: true },
        buttonClass: { default: "" },
        buttonTabindex: { default: undefined },
        disabled: { type: Boolean },
        ariaDisabled: { type: Boolean },
        error: { default: "" },
        iconSrc: { default: "/svg/svg-defs.svg" },
        menuClass: { default: "" },
        menuMaxHeight: { default: 0 },
        menuMinHeight: { default: 0 },
        menuPosition: { default: "" },
        menuWidth: { default: 0 },
        menuWidthMatchButton: { type: Boolean },
        modelValue: { type: Boolean },
        offsetY: { default: 3 },
        placement: { default: "auto" },
        prependIcon: { default: "" },
        prependIconInside: { default: "" },
        buttonOnly: { type: Boolean },
        type: { default: "custom" },
        useVShow: { type: Boolean },
        windowPadding: { default: 20 },
        ignoreClickOutside: { default: () => [] },
        active: { type: Boolean },
        tooltip: { default: "" },
        align: { default: undefined },
        lockPositionOnOpen: { type: Boolean, default: false },
        buttonAttrs: { default: () => ({}) },
        menuRole: { default: "menu" },
        menuId: {}
    },
    emits: ["update:modelValue", "click"],
    setup(__props, { expose: __expose, emit: __emit }) {
        /********************************************
         * DropdownMenu Component
         * Provides a clickable trigger and a positioned menu.
         *
         * Pass in two slots for the menu content
         *  <template #button></template>
         *  <template #menu></template>
         *
         * Props:
         *  absolute: Controls whether the menu is positioned absolutely (default: fixed positioning). Can be used to fix issues with menus inside scrollable containers
         *  appendIcon: Icon to be appended to the button
         *  appendIconInside: Icon to be appended to the button text
         *  autoClose: Controls whether the menu automatically closes when an item is clicked. To handle this selectively, prevent the propogation of click events
         *  buttonClass: Classes to be applied to the button element
         *  disabled: Disables the button
         *  ariaDisabled: Marks the button aria-disabled (stays focusable, tooltip still fires, click is a no-op) instead of natively disabled
         *  error: Error message to be displayed
         *  iconSrc: Path to the svg icon sprite
         *  ignoreClickOutside: Ignore clicks outside the menu on the specified element (valid css selector)
         *  menuClass: Classes to be applied to the menu element
         *  menuMaxHeight: Maximum height of the menu
         *  menuPosition: Controls the position of the menu relative to the button
         *  menuWidth: Width of the menu
         *  modelValue: Controls the open state of the menu
         *  offsetY: Vertical offset of the menu from the button
         *  placement: Controls the vertical placement of the menu. Options are "above", "below", and "auto" (default)
         *  prependIcon: Icon to be prepended to the button
         *  prependIconInside: Icon to be prepended to the button text
         *  buttonOnly: Controls whether the menu is in static mode
         *  type: Controls the type of dropdown menu. Options are "button", "flyout", "select", and "custom"
         *  useVShow: Controls whether the menu is shown using v-show or v-if
         *  windowPadding: Padding around the menu
         *
         * Emits:
         *  modelValue: indicates that the open state of the menu has changed
         *
         *  <DropdownMenu type="button" button-class="btn-primary">
         *    <template #button>Options</template>
         *    <template #menu>
         *      <button @click="handleEdit">Edit</button>
         *      <DropdownMenu type="flyout">
         *        <template #button>
         *          Flyout Button Label
         *        </template>
         *        <template #menu>
         *          <button type="button" class="rec-dropdown-menu__item">
         *            Flyout Menu Button Label
         *          </button>
         *        </template>
         *      </DropdownMenu>
         *    </template>
         *  </DropdownMenu>
         *********************************************/
        const props = __props;
        //Emits
        const emit = __emit;
        const model = useVModel(props, "modelValue", emit, {
            defaultValue: false,
            passive: true
        });
        const key = uuid();
        const dropdown = ref(null);
        const menu = ref();
        const menuButton = ref(null);
        // Top-level composables for reactive element bounds
        const { update: updateButtonBounds, ...buttonBounds } = useElementBounding(menuButton);
        const { update: updateMenuBounds, ...menuBounds } = useElementBounding(menu);
        const { width: windowWidth, height: windowHeight } = useWindowSize({ includeScrollbar: false });
        const isButton = computed(() => props.type == "button");
        const isFlyout = computed(() => props.type == "flyout");
        const isSelect = computed(() => props.type == "select");
        const isFilterChip = computed(() => props.type == "filter-chip");
        const menuHeight = ref(600);
        const lockedPosition = ref(null);
        const classes = computed(() => ({
            "rec-dropdown": true,
            "rec-dropdown--open": model.value,
            "rec-dropdown--absolute": props.absolute,
            "rec-dropdown--with-icons": props.prependIcon || props.appendIcon,
            "rec-dropdown--with-icons-inside": props.prependIconInside || props.appendIconInside
        }));
        const buttonClasses = computed(() => {
            const classes = [];
            const builtin = isFlyout.value
                ? {
                    "rec-dropdown-menu__item rec-dropdown-menu__item--flyout": true,
                    "rec-dropdown-menu__item--active": model.value
                }
                : {
                    "rec-dropdown__button": true,
                    "rec-dropdown__button--has-tooltip": !!props.tooltip,
                    [`rec-dropdown__button--${props.type}`]: true,
                    "rec-dropdown__button--active": props.active, // Active state (e.g. values are selected in a RecSelect component)
                    "rec-dropdown__button--disabled": props.disabled,
                    btn: isButton.value,
                    "form-control": isSelect.value,
                    "rec-filter-chip": isFilterChip.value
                };
            classes.push(builtin);
            if (props.buttonClass)
                classes.push(props.buttonClass);
            return classes;
        });
        const menuClasses = computed(() => ["rec-dropdown-menu", props.menuClass || ""]);
        onClickOutside(menu, () => {
            if (!model.value)
                return;
            handleToggleMenuOpen(false);
        }, {
            ignore: [menuButton, ...props.ignoreClickOutside]
        });
        const handleToggleMenuOpen = (state) => {
            // aria-disabled blocks opening only — closing (clickOutside / autoClose / Escape) must still work.
            const willOpen = state ?? !model.value;
            if (props.ariaDisabled && willOpen)
                return;
            if (props.buttonOnly) {
                emit("click");
                return;
            }
            model.value = state !== undefined ? state : !model.value;
        };
        const onMenuClick = () => {
            if (!props.autoClose)
                return;
            handleToggleMenuOpen(false);
        };
        const menuStyle = ref({
            top: undefined,
            bottom: undefined,
            left: undefined,
            right: undefined,
            maxHeight: undefined,
            minHeight: undefined,
            width: undefined,
            maxWidth: undefined
        });
        onMounted(() => {
            if (props.menuMaxHeight > 0) {
                menuStyle.value.maxHeight = toCssSizeUnit(props.menuMaxHeight);
            }
            if (props.menuMinHeight > 0) {
                menuStyle.value.minHeight = toCssSizeUnit(props.menuMinHeight);
            }
        });
        /**
         * Get button bounds, using locked position if available
         */
        const getButtonBounds = () => {
            if (props.lockPositionOnOpen && lockedPosition.value && model.value) {
                return lockedPosition.value;
            }
            const bounds = {
                x: buttonBounds.x.value,
                width: buttonBounds.width.value,
                height: buttonBounds.height.value,
                right: buttonBounds.right.value,
                top: buttonBounds.top.value,
                bottom: buttonBounds.bottom.value
            };
            // Lock position when dropdown opens
            if (props.lockPositionOnOpen && model.value && !lockedPosition.value) {
                lockedPosition.value = bounds;
            }
            return bounds;
        };
        /**
         * Apply menu width based on props
         */
        const applyMenuWidth = (buttonWidth) => {
            if (props.menuWidth > 0) {
                menuStyle.value.width = toCssSizeUnit(props.menuWidth);
                menuStyle.value.maxWidth = toCssSizeUnit(props.menuWidth);
            }
            else if (isSelect.value || (props.menuWidthMatchButton && !!buttonWidth)) {
                menuStyle.value.width = toCssSizeUnit(buttonWidth);
                menuStyle.value.maxWidth = toCssSizeUnit(buttonWidth);
            }
        };
        // Gap (px) between a flyout trigger and its menu
        const calculateBaseY = (button, offsetTop) => {
            let y = button.bottom + offsetTop;
            if (props.absolute) {
                y = button.height + props.offsetY;
            }
            // Handle parent-top alignment for flyouts
            if (isFlyout.value && props.align === "parent-top") {
                const parentMenu = menuButton.value?.closest(".rec-dropdown-menu[role=menu]");
                if (parentMenu) {
                    const { top } = parentMenu.getBoundingClientRect();
                    y = top;
                }
            }
            return y;
        };
        /**
         * Position menu horizontally using logical properties (RTL-aware)
         */
        const positionHorizontally = (ctx, baseY, baseOffsetTop) => {
            const { button, menuWidth, isRtl, inlineStart, inlineEnd } = ctx;
            let y = baseY;
            let offsetTop = baseOffsetTop;
            const x = (isFlyout.value ? button.right : button.x) + window.scrollX;
            // Check if menu would overflow on the inline-end side
            const wouldOverflowInlineEnd = isRtl
                ? button.right - menuWidth < props.windowPadding
                : x + menuWidth > windowWidth.value;
            // menuPosition "left"/"right" are logical start/end: anchor the opposite edge to open toward that side (mirrors in RTL).
            const forceInlineEndAlign = props.menuPosition?.includes("left");
            if (isFlyout.value) {
                // A flyout cascades to the side of its trigger toward inline-end (physical right in LTR,
                // left in RTL); it flips to the inline-start side only when the natural side overflows.
                const inlineEndCascadeOverflows = isRtl
                    ? button.x - menuWidth < props.windowPadding
                    : button.right + menuWidth > windowWidth.value;
                const inlineStartCascadeFits = isRtl
                    ? button.right + menuWidth <= windowWidth.value - props.windowPadding
                    : button.x - menuWidth >= 0;
                if ((inlineEndCascadeOverflows || forceInlineEndAlign) && (inlineStartCascadeFits || props.absolute)) {
                    // Flip: menu's inline-end edge meets the trigger's inline-start edge
                    menuStyle.value[inlineStart] = "";
                    if (props.absolute) {
                        menuStyle.value[inlineEnd] = toCssSizeUnit(button.x + button.width - windowWidth.value);
                    }
                    else {
                        menuStyle.value[inlineEnd] = toCssSizeUnit(isRtl ? button.right : windowWidth.value - button.x);
                    }
                }
                else if (inlineEndCascadeOverflows || forceInlineEndAlign) {
                    // Neither side fits, fall back to window padding
                    y = button.bottom;
                    offsetTop = button.height;
                    menuStyle.value[inlineEnd] = "";
                    menuStyle.value[inlineStart] = toCssSizeUnit(props.windowPadding);
                }
                else {
                    // Default: menu's inline-start edge meets the trigger's inline-end edge
                    menuStyle.value[inlineEnd] = "";
                    if (props.absolute) {
                        menuStyle.value[inlineStart] = "0";
                    }
                    else {
                        menuStyle.value[inlineStart] = toCssSizeUnit(isRtl ? windowWidth.value - button.x : x);
                    }
                }
            }
            else if (wouldOverflowInlineEnd || forceInlineEndAlign) {
                // Non-flyout overflow: try aligning to inline-end instead
                const canFitInlineEnd = isRtl
                    ? button.x + menuWidth <= windowWidth.value - props.windowPadding
                    : button.x + button.width - menuWidth >= 0;
                if (!canFitInlineEnd && !props.absolute) {
                    // Menu cannot fit either way, fall back to window padding
                    y = button.bottom + offsetTop;
                    menuStyle.value[inlineEnd] = "";
                    menuStyle.value[inlineStart] = toCssSizeUnit(props.windowPadding);
                }
                else {
                    // Align menu's inline-end with button's inline-end
                    menuStyle.value[inlineStart] = "";
                    if (props.absolute) {
                        menuStyle.value[inlineEnd] = isRtl ? "0" : toCssSizeUnit(button.x + button.width - windowWidth.value);
                    }
                    else {
                        const pos = isRtl ? button.x : windowWidth.value - button.right;
                        menuStyle.value[inlineEnd] = toCssSizeUnit(pos);
                    }
                }
            }
            else {
                // Default: align menu's inline-start with button's inline-start
                menuStyle.value[inlineEnd] = "";
                if (props.absolute) {
                    menuStyle.value[inlineStart] = "0";
                }
                else {
                    const pos = isRtl ? windowWidth.value - button.right : x;
                    menuStyle.value[inlineStart] = toCssSizeUnit(pos);
                }
            }
            return { y, offsetTop };
        };
        /**
         * Position menu vertically (above or below button)
         */
        const positionVertically = (ctx, y, offsetTop) => {
            const { button, menuHeight } = ctx;
            let shouldPositionAbove;
            if (props.placement === "above") {
                shouldPositionAbove = true;
            }
            else if (props.placement === "below") {
                shouldPositionAbove = false;
            }
            else {
                // Auto placement - check if menu fits below button
                shouldPositionAbove = y + menuHeight > windowHeight.value - props.windowPadding;
            }
            if (shouldPositionAbove) {
                // Position above button
                menuStyle.value.top = toCssSizeUnit(Math.max(props.windowPadding, button.top - offsetTop - menuHeight));
                if (menuHeight > windowHeight.value - 2 * props.windowPadding) {
                    menuStyle.value.maxHeight = toCssSizeUnit(windowHeight.value - 2 * props.windowPadding);
                }
            }
            else {
                // Position below button
                menuStyle.value.top = toCssSizeUnit(y);
                if (y + menuHeight > windowHeight.value - props.windowPadding) {
                    menuStyle.value.maxHeight = toCssSizeUnit(windowHeight.value - y - props.windowPadding);
                }
            }
        };
        /**
         * Main positioning orchestrator
         */
        const positionMenu = () => {
            if (!menu.value || !menuButton.value)
                return;
            // Force synchronous measurement of element bounds
            updateButtonBounds();
            updateMenuBounds();
            const button = getButtonBounds();
            if (!button)
                return;
            const menuWidth = menuBounds.width.value;
            const menuHeight = menuBounds.height.value;
            applyMenuWidth(button.width);
            // Detect RTL mode and define logical directions
            const isRtl = !!window.Ramius?.is_rtl;
            const inlineStart = isRtl ? "right" : "left";
            const inlineEnd = isRtl ? "left" : "right";
            const ctx = {
                button,
                menuWidth,
                menuHeight,
                isRtl,
                inlineStart,
                inlineEnd
            };
            const offsetTop = isFlyout.value ? -button.height - FLYOUT_OFFSET_GAP : props.offsetY;
            const baseY = calculateBaseY(button, offsetTop);
            const { y, offsetTop: finalOffsetTop } = positionHorizontally(ctx, baseY, offsetTop);
            positionVertically(ctx, y, finalOffsetTop);
        };
        // Watch reactive sources that should trigger repositioning
        // flush: 'post' ensures DOM is updated before positioning runs
        watch([
            // all button bounds positionMenu reads: an in-place resize changes width/right without moving x/y
            buttonBounds.x,
            buttonBounds.y,
            buttonBounds.width,
            buttonBounds.height,
            buttonBounds.right,
            buttonBounds.top,
            buttonBounds.bottom,
            menuBounds.width,
            menuBounds.height,
            windowWidth,
            windowHeight,
            model
        ], positionMenu, { immediate: true, flush: "post" });
        // Follow the trigger each frame while open: useElementBounding misses position-only moves (RTL flip, reflow).
        const { pause: pauseTriggerFollow, resume: resumeTriggerFollow } = useRafFn(updateButtonBounds, { immediate: false });
        watch(model, (isOpen) => (isOpen && !props.lockPositionOnOpen ? resumeTriggerFollow() : pauseTriggerFollow()), {
            immediate: true
        });
        // Clear locked position when dropdown closes
        watch(model, (isOpen) => {
            if (!isOpen && props.lockPositionOnOpen) {
                lockedPosition.value = null;
            }
            // Remove focus from button when menu closes
            if (!isOpen && menuButton.value) {
                menuButton.value.blur();
            }
        });
        // Evaluate the style after an orientation change, with a delay to allow for animations to complete
        const { orientation } = useScreenOrientation();
        watch(orientation, () => {
            setTimeout(() => {
                if (!menu.value)
                    return;
                positionMenu();
            }, 500);
        });
        watchEffect((onCleanup) => {
            const eventListenerDisposer = useDropdownAccessibility({
                eventTargetEl: dropdown,
                menu: menu,
                isOpen: model,
                toggleSubMenu: handleToggleMenuOpen,
                menuToggleBtn: menuButton,
                submenu: isFlyout.value ? menu : undefined,
                menuItemBtnType: isFlyout.value ? MenuItemType.FLYOUT : MenuItemType.DROPDOWN
            });
            onCleanup(() => eventListenerDisposer());
        });
        __expose({
            menuButton,
            el: dropdown
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", {
                ref_key: "dropdown",
                ref: dropdown,
                key: _unref(key),
                class: _normalizeClass(classes.value)
            }, [
                (_ctx.error)
                    ? (_openBlock(), _createElementBlock("span", {
                        key: 0,
                        class: "label label-danger rc-mb5",
                        textContent: _toDisplayString(_ctx.error)
                    }, null, 8, _hoisted_1))
                    : _createCommentVNode("", true),
                (_ctx.prependIcon)
                    ? (_openBlock(), _createBlock(SvgIcon, {
                        key: 1,
                        icon: _ctx.prependIcon,
                        src: _ctx.iconSrc,
                        class: "rec-dropdown__prepend-icon"
                    }, null, 8, ["icon", "src"]))
                    : _createCommentVNode("", true),
                _withDirectives((_openBlock(), _createElementBlock("button", _mergeProps({
                    ref_key: "menuButton",
                    ref: menuButton,
                    class: buttonClasses.value,
                    disabled: !!_ctx.disabled,
                    "aria-disabled": _ctx.ariaDisabled || undefined,
                    tabindex: _ctx.buttonTabindex,
                    "aria-haspopup": "true",
                    type: "button"
                }, _ctx.buttonAttrs, {
                    onClick: _cache[0] || (_cache[0] = _withModifiers(($event) => (handleToggleMenuOpen()), ["stop"]))
                }), [
                    (_ctx.prependIconInside)
                        ? (_openBlock(), _createBlock(SvgIcon, {
                            key: 0,
                            icon: _ctx.prependIconInside,
                            src: _ctx.iconSrc,
                            class: "rec-dropdown__prepend-icon-inside"
                        }, null, 8, ["icon", "src"]))
                        : _createCommentVNode("", true),
                    _renderSlot(_ctx.$slots, "button", _normalizeProps(_guardReactiveProps({ open: _unref(model) }))),
                    (_ctx.appendIconInside)
                        ? (_openBlock(), _createBlock(SvgIcon, {
                            key: 1,
                            icon: _ctx.appendIconInside,
                            src: _ctx.iconSrc,
                            class: "rec-dropdown__append-icon-inside"
                        }, null, 8, ["icon", "src"]))
                        : _createCommentVNode("", true),
                    (isFlyout.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                            _createElementVNode("div", null, [
                                _createVNode(SvgIcon, {
                                    icon: "icon-arrow-right",
                                    class: "rec-flyout-indicator__chevron"
                                })
                            ])
                        ]))
                        : _createCommentVNode("", true)
                ], 16, _hoisted_2)), [
                    [_unref(vTooltip), _ctx.tooltip]
                ]),
                (_ctx.appendIcon)
                    ? (_openBlock(), _createBlock(SvgIcon, {
                        key: 2,
                        icon: _ctx.appendIcon,
                        src: _ctx.iconSrc,
                        class: "rec-dropdown__append-icon"
                    }, null, 8, ["icon", "src"]))
                    : _createCommentVNode("", true),
                (_ctx.useVShow ? true : _unref(model) && _ctx.$slots.menu)
                    ? _withDirectives((_openBlock(), _createElementBlock("div", {
                        key: 3,
                        ref_key: "menu",
                        ref: menu,
                        id: _ctx.menuId,
                        class: _normalizeClass(menuClasses.value),
                        style: _normalizeStyle(menuStyle.value),
                        role: _ctx.menuRole,
                        onClick: _withModifiers(onMenuClick, ["stop"])
                    }, [
                        _renderSlot(_ctx.$slots, "menu", _normalizeProps(_guardReactiveProps({ toggleMenu: handleToggleMenuOpen, menuHeight: menuHeight.value })))
                    ], 14, _hoisted_4)), [
                        [_vShow, _ctx.useVShow ? _unref(model) && _ctx.$slots.menu : true]
                    ])
                    : _createCommentVNode("", true)
            ], 2));
        };
    }
});
